import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { 
  Users, Activity, DollarSign, AlertTriangle, TrendingUp, 
  Calendar, MessageSquare, Phone, Video, Settings, ArrowLeft,
  Sparkles, FileText,
  ShieldCheck, LogOut,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [careGrades, setCareGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [edits, setEdits] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, usersRes, gradesRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/care-grades/all`, { headers })
      ]);

      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setCareGrades(gradesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/provider-dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleGradeStatus = async (code, currentStatus) => {
    try {
      await axios.put(
        `${API}/care-grades/${code}`,
        { enabled: !currentStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`Care grade ${code} ${!currentStatus ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update care grade');
    }
  };

  const saveGradePricing = async (code, fields) => {
    try {
      await axios.put(
        `${API}/care-grades/${code}`,
        fields,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`${code} updated`);
      setEdits((prev) => { const n = { ...prev }; delete n[code]; return n; });
      fetchData();
    } catch (error) {
      toast.error('Failed to update care grade');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="spinner"></div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color, trend }) => (
    <div className="card p-6">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
          <Icon size={24} style={{ color }} />
        </div>
        {trend && (
          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
            +{trend}%
          </span>
        )}
      </div>
      <div className="text-3xl font-heading font-bold text-[#2D3748] mb-1">{value}</div>
      <div className="text-sm text-[#718096]">{label}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-primary text-white p-4 sticky top-0 z-10">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/provider-dashboard')} className="p-1 shrink-0">
              <ArrowLeft size={24} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-heading font-semibold truncate">Admin Dashboard</h1>
              <p className="text-sm opacity-80 hidden sm:block">Platform Management & Analytics</p>
            </div>
          </div>
          <div className="flex items-center flex-wrap justify-end gap-1 sm:gap-3">
            <button 
              onClick={() => navigate('/admin/users')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="User Management"
            >
              <Users size={20} />
            </button>
            <button 
              onClick={() => navigate('/escalations')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="Escalations"
            >
              <AlertTriangle size={20} />
            </button>
            <button 
              onClick={() => navigate('/admin/wellness-partners')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="Wellness Partners"
            >
              <Sparkles size={20} />
            </button>
            <button
              onClick={() => navigate('/admin/scheduling')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="Scheduling"
              data-testid="admin-scheduling-nav"
            >
              <Calendar size={20} />
            </button>
            <button
              onClick={() => navigate('/admin/payments')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="Payments & Reconciliation"
              data-testid="admin-payments-nav"
            >
              <DollarSign size={20} />
            </button>
            <button
              onClick={() => navigate('/admin/privacy-requests')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="Data Requests"
              data-testid="admin-privacy-nav"
            >
              <ShieldCheck size={20} />
            </button>
            <button 
              onClick={() => navigate('/admin/settings')}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg"
              title="Settings"
            >
              <Settings size={20} />
            </button>
            <button
              onClick={() => { logout(); navigate('/auth'); }}
              className="flex items-center gap-1.5 ml-1 sm:ml-2 pl-2 sm:pl-3 border-l border-white/30 py-1.5 px-2 sm:px-3 rounded-lg hover:bg-white/10 transition-colors"
              title="Log out"
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} />
              <span className="text-sm font-medium hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto flex gap-2 sm:gap-4 px-4 overflow-x-auto">
          {['overview', 'users', 'care-grades'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-3 sm:px-4 border-b-2 transition-colors capitalize whitespace-nowrap ${
                activeTab === tab
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-[#718096] hover:text-primary'
              }`}
            >
              {tab.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid md:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Total Users"
                value={analytics.total_users}
                color="#5B8C79"
              />
              <StatCard
                icon={MessageSquare}
                label="Total Sessions"
                value={analytics.total_sessions}
                color="#64B5F6"
              />
              <StatCard
                icon={Calendar}
                label="Appointments"
                value={analytics.total_appointments}
                color="#E89A8E"
              />
              <StatCard
                icon={Users}
                label="Anonymous Users"
                value={analytics.total_masked_profiles}
                color="#F6E05E"
              />
            </div>

            {/* Sessions by Grade */}
            <div className="card p-6">
              <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4 flex items-center gap-2">
                <Activity size={20} className="text-primary" />
                Sessions by Care Grade
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                {Object.entries(analytics.sessions_by_grade).map(([grade, count]) => (
                  <div key={grade} className="p-4 bg-[#FAF9F6] rounded-xl">
                    <div className="text-2xl font-bold text-primary mb-1">{count}</div>
                    <div className="text-sm text-[#718096]">{grade}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Distribution */}
            <div className="card p-6">
              <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4 flex items-center gap-2">
                <AlertTriangle size={20} className="text-amber-500" />
                Risk Level Distribution
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {Object.entries(analytics.risk_distribution).map(([risk, count]) => {
                  const colors = {
                    low: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
                    medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
                    high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
                  };
                  const color = colors[risk];
                  return (
                    <div key={risk} className={`p-4 ${color.bg} border ${color.border} rounded-xl`}>
                      <div className={`text-3xl font-bold ${color.text} mb-1`}>{count}</div>
                      <div className={`text-sm ${color.text} capitalize`}>{risk} Risk</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tele-MANAS Events */}
            <div className="card p-6">
              <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-2">
                Tele-MANAS Crisis Events
              </h3>
              <div className="text-4xl font-bold text-destructive mb-2">
                {analytics.tele_manas_events}
              </div>
              <p className="text-sm text-[#718096]">
                Total crisis escalations to Tele-MANAS
              </p>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-heading font-semibold text-[#2D3748]">
                All Users ({users.length})
              </h2>
            </div>
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#2D3748]">{user.name}</h4>
                      <p className="text-sm text-[#718096]">{user.email}</p>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full capitalize">
                          {user.role.replace('_', ' ')}
                        </span>
                        {user.role === 'care_provider' && user.specialization && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                            {user.specialization}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="text-sm px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50">
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Care Grades Tab */}
        {activeTab === 'care-grades' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-[#2D3748]">
                Care Grades Management
              </h2>
              <p className="text-sm text-[#718096] mt-1">
                Configure pricing and duration for each grade. Prices are set here during clinic setup — update before going live.
              </p>
            </div>

            {[
              { key: 'anonymous', title: 'Anonymous Sessions', codes: ['G0', 'G1'] },
              { key: 'therapy', title: 'Therapy Sessions', codes: ['G3', 'G4', 'G5'] },
              { key: 'extensions', title: 'Session Extensions', codes: ['E1', 'E2'] },
            ].map((group) => {
              const groupGrades = careGrades.filter((g) => group.codes.includes(g.code));
              if (groupGrades.length === 0) return null;
              return (
                <div key={group.key} className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#718096]">{group.title}</h3>
                  {groupGrades.map((grade) => {
                    const Icon = grade.channel === 'chat' ? MessageSquare : grade.channel === 'voice' ? Phone : Video;
                    const edit = edits[grade.code] || {};
                    const isTherapy = grade.grade_type === 'session' && grade.tier;
                    const priceVal = edit.base_price ?? grade.base_price ?? '';
                    const durVal = edit.duration_minutes ?? grade.duration_minutes ?? '';
                    const dirty = (edit.base_price !== undefined && Number(edit.base_price) !== Number(grade.base_price))
                      || (edit.duration_minutes !== undefined && Number(edit.duration_minutes) !== Number(grade.duration_minutes));
                    return (
                      <div key={grade.code} className="card p-5" data-testid={`care-grade-${grade.code}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex items-start gap-4 min-w-0">
                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                              <Icon size={24} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-3 mb-1 flex-wrap">
                                <h4 className="font-heading font-semibold text-[#2D3748] text-lg">
                                  {grade.code}: {grade.name}
                                </h4>
                                <span className={`text-xs px-2 py-1 rounded-full ${grade.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                  {grade.enabled ? 'Active' : 'Disabled'}
                                </span>
                              </div>
                              <p className="text-sm text-[#718096]">{grade.description}</p>
                              <p className="text-xs text-[#718096] mt-1 capitalize">Channel: {grade.channel}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleGradeStatus(grade.code, grade.enabled)}
                            data-testid={`toggle-grade-${grade.code}`}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${grade.enabled ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                          >
                            {grade.enabled ? 'Disable' : 'Enable'}
                          </button>
                        </div>

                        <div className="flex items-end gap-4 mt-4 flex-wrap">
                          <div>
                            <label className="block text-xs text-[#718096] mb-1">Price (₹)</label>
                            <input
                              type="number" min="0"
                              data-testid={`grade-price-${grade.code}`}
                              value={priceVal}
                              onChange={(e) => setEdits((p) => ({ ...p, [grade.code]: { ...p[grade.code], base_price: e.target.value } }))}
                              className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-[#718096] mb-1">
                              Duration (min){!isTherapy && grade.grade_type !== 'session' ? '' : ''}
                            </label>
                            <input
                              type="number" min="1"
                              data-testid={`grade-duration-${grade.code}`}
                              value={durVal}
                              disabled={!isTherapy}
                              title={!isTherapy ? 'Duration is fixed for this grade' : ''}
                              onChange={(e) => setEdits((p) => ({ ...p, [grade.code]: { ...p[grade.code], duration_minutes: e.target.value } }))}
                              className={`w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm ${!isTherapy ? 'bg-gray-50 text-gray-400' : ''}`}
                            />
                          </div>
                          <button
                            onClick={() => {
                              const fields = {};
                              if (edit.base_price !== undefined) fields.base_price = Number(edit.base_price);
                              if (isTherapy && edit.duration_minutes !== undefined) fields.duration_minutes = Number(edit.duration_minutes);
                              if (Object.keys(fields).length) saveGradePricing(grade.code, fields);
                            }}
                            disabled={!dirty}
                            data-testid={`save-grade-${grade.code}`}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${dirty ? 'bg-primary text-white hover:opacity-90' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}